import { type GitLinkSubjectKind } from '@apm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { GlassSegmented } from '../../../components/ui/glass-segmented';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/states';
import { errorMessage } from '../../../lib/error-message';
import { type GitTargetRef, gitApi, gitKeys } from '../api';
import { parseLinkSubject } from '../git-format';

const KINDS = [
  'change_request',
  'branch',
  'commit',
] as const satisfies readonly GitLinkSubjectKind[];

/**
 * Form **gắn link tay** của khối "Phát triển" (`git:act`) — APM-GIT-e,
 * docs/plan-git-integration.md §4.1, §8.2: gắn một MR / nhánh / commit **không ghi
 * mã việc** vào việc này. Là quyết định của người ⇒ API ghi `git_link_override`
 * và event `task.git_link_added` / `issue.git_link_added`.
 *
 * Thứ được gắn phải **đã có trong dữ liệu đã kéo về** của kho; chưa có thì API trả
 * câu "chờ lượt kéo kế" và nó hiện nguyên ở đây.
 *
 * ⚠️ Không nhận asOf: đây là form **ghi** — nó chỉ được mở khi đang xem "bây giờ"
 * (khối "Phát triển" ẩn mọi thao tác khi có `asOf`). Danh sách kho nó đọc là cấu
 * hình hiện hành (§7.3).
 */
export function GitManualLinkForm({
  projectId,
  target,
  onDone,
}: {
  readonly projectId: string;
  readonly target: GitTargetRef;
  readonly onDone: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<GitLinkSubjectKind>('change_request');
  const [repositoryId, setRepositoryId] = useState('');
  const [text, setText] = useState('');
  const [touched, setTouched] = useState(false);

  const repositories = useQuery({
    queryKey: gitKeys.repositories(projectId),
    queryFn: () => gitApi.repositories(projectId),
  });

  const add = useMutation({
    mutationFn: (input: { repositoryId: string; subjectRef: string }) =>
      gitApi.addLinkOverride(projectId, {
        targetKind: target.kind,
        targetId: target.id,
        repositoryId: input.repositoryId,
        subjectKind: kind,
        subjectRef: input.subjectRef,
        action: 'add',
      }),
    onSuccess: () => {
      toast.success(t('gitTab.development.manual.added'));
      void queryClient.invalidateQueries({ queryKey: gitKeys.project(projectId) });
      onDone();
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  if (repositories.isPending) return <Skeleton className="h-16 w-full" />;
  // Lỗi đọc danh sách kho cũng là "chưa gắn được" — nói cùng một câu, không một khung đỏ trong drawer.
  if (repositories.isError || repositories.data.length === 0) {
    return (
      <p role="status" className="text-xs text-muted-foreground">
        {t('gitTab.development.manual.noRepository')}
      </p>
    );
  }

  const repository = repositoryId || repositories.data[0]!.id;
  const subjectRef = parseLinkSubject(kind, text);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    setTouched(true);
    if (subjectRef === null || add.isPending) return;
    add.mutate({ repositoryId: repository, subjectRef });
  };

  return (
    <form
      className="flex flex-col gap-2 rounded-xl border border-border p-3"
      onSubmit={submit}
      aria-label={t('gitTab.development.manual.title')}
    >
      <GlassSegmented
        size="sm"
        className="self-start"
        ariaLabel={t('gitTab.development.manual.kind')}
        value={kind}
        options={KINDS.map((value) => ({
          value,
          label: t(`gitTab.development.manual.kindOption.${value}`),
        }))}
        onChange={(next) => {
          setKind(next);
          setTouched(false);
        }}
      />
      {repositories.data.length > 1 ? (
        <select
          className="h-9 max-w-full rounded-[12px] border border-border-strong bg-background px-3 text-sm"
          aria-label={t('gitTab.common.repositoryFilter')}
          value={repository}
          onChange={(event) => setRepositoryId(event.target.value)}
        >
          {repositories.data.map((item) => (
            <option key={item.id} value={item.id}>
              {item.fullPath}
            </option>
          ))}
        </select>
      ) : null}
      <Input
        className="rounded-[12px]"
        value={text}
        onChange={(event) => setText(event.target.value)}
        placeholder={t(`gitTab.development.manual.placeholder.${kind}`)}
        aria-label={t(`gitTab.development.manual.placeholder.${kind}`)}
        aria-invalid={touched && subjectRef === null}
        spellCheck={false}
      />
      {touched && subjectRef === null ? (
        <p role="alert" className="text-xs text-destructive">
          {t(`gitTab.development.manual.invalid.${kind}`)}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" size="sm" disabled={add.isPending || !text.trim()}>
          {add.isPending ? t('common.saving') : t('gitTab.development.manual.submit')}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}
